import {
  useFeatureFlagValue,
  useFeatureFlagValueBool,
} from "@mirage/service-experimentation/useFeatureFlagValue";
import useSettings from "@mirage/service-settings/useSettings";
import { atom, useAtom } from "jotai";

import type { FeatureValue } from "@mirage/service-experimentation/features";
import type { TabIds } from "@mirage/service-settings/service/types";
import { useHydrateAtoms } from "jotai/utils";

const startPageSelectedTabAtom = atom<TabIds>("recents");

function convertRecentsABTestVariantToTabIds(
  recentsABTestVariant: FeatureValue,
): TabIds | undefined {
  const variantString = String(recentsABTestVariant);

  if (
    variantString === "recents" ||
    variantString === "activity" ||
    variantString === "stacks"
  ) {
    return variantString;
  }
}

export const useSelectedTab = () => {
  const { settings, set: setSettings } = useSettings([
    "startPageSelectedTab",
    "startPageSelectedTabExperimentDone",
  ]);
  const {
    startPageSelectedTab: startPageSelectedTabFromSettings,
    startPageSelectedTabExperimentDone,
  } = settings ?? {};

  const isStacksPageTabFeatureEnabled = useFeatureFlagValueBool(
    "dash_web_2025_03_27_start_page_tabs",
  );
  const startPageSelectedTabFromGrowthbook = useFeatureFlagValue(
    "dash_2025_06_17_start_page_tab",
  );
  const isExperimentRunning = startPageSelectedTabFromGrowthbook !== "OFF";

  const DEFAULT_FIRST_TAB = isStacksPageTabFeatureEnabled
    ? "activity"
    : "recents";
  let initialValue = startPageSelectedTabFromSettings || DEFAULT_FIRST_TAB;

  // Edge case that I'm not sure would happen in prod but is in meticulous: stack page is disabled but the stored settings value is 'activity' which is not rendered
  if (
    !isStacksPageTabFeatureEnabled &&
    startPageSelectedTabFromSettings === "activity"
  ) {
    initialValue = "recents";
  }

  useHydrateAtoms([[startPageSelectedTabAtom, initialValue]]);

  const [startPageSelectedTab, _setStartPageSelectedTab] = useAtom(
    startPageSelectedTabAtom,
  );

  const setStartPageSelectedTab = (tabId: TabIds) => {
    _setStartPageSelectedTab(tabId);
    setSettings("startPageSelectedTab", tabId);
  };

  if (isExperimentRunning) {
    const tabIdFromGrowthbook = convertRecentsABTestVariantToTabIds(
      startPageSelectedTabFromGrowthbook,
    );
    if (!startPageSelectedTabExperimentDone && tabIdFromGrowthbook) {
      setStartPageSelectedTab(tabIdFromGrowthbook);
      setSettings("startPageSelectedTabExperimentDone", 1);
    }
  }

  return {
    startPageSelectedTab,
    setStartPageSelectedTab,
  };
};
